// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Read-only client for the D6 content library API (/api/v1/...). The response
// parsers are pure (functional core) and unit-tested without a network; the
// fetch wrappers are the imperative shell and fail soft to empty/null so the
// "start from a template" picker is an optional enhancement, never a blocker.

const API_BASE = '/api/v1';

export interface TemplateSummary {
  slug: string;
  name: string;
  description?: string;
}

export interface TemplateDetail extends TemplateSummary {
  // The starter model as XMILE text, used to seed a new project.
  modelXmile: string;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

// Parse the `{data: [...]}` list envelope into template summaries, skipping any
// malformed entry (missing slug or name). Pure.
export function parseTemplateSummaries(payload: unknown): TemplateSummary[] {
  const data = (payload as { data?: unknown } | null | undefined)?.data;
  if (!Array.isArray(data)) {
    return [];
  }
  const summaries: TemplateSummary[] = [];
  for (const row of data) {
    const r = (row ?? {}) as Record<string, unknown>;
    const slug = asString(r.slug);
    const name = asString(r.name);
    if (slug && name) {
      summaries.push({ slug, name, description: asString(r.description) });
    }
  }
  return summaries;
}

// Parse the `{data: {...}}` item envelope into a template detail, or null when
// it lacks the fields needed to seed a project (slug, name, modelXmile). Pure.
export function parseTemplateDetail(payload: unknown): TemplateDetail | null {
  const data = (payload as { data?: unknown } | null | undefined)?.data as Record<string, unknown> | undefined;
  if (!data) {
    return null;
  }
  const slug = asString(data.slug);
  const name = asString(data.name);
  const modelXmile = asString(data.modelXmile);
  if (!slug || !name || !modelXmile) {
    return null;
  }
  return { slug, name, modelXmile, description: asString(data.description) };
}

export type ContentResource = 'templates' | 'archetypes' | 'sectors';

// Fetch the first page of a content resource's list (summaries). Returns [] on
// any failure -- callers treat the library as an optional enhancement.
export async function fetchContentList(resource: ContentResource): Promise<TemplateSummary[]> {
  try {
    const res = await fetch(`${API_BASE}/${resource}`, { credentials: 'same-origin' });
    if (!res.ok) {
      return [];
    }
    return parseTemplateSummaries(await res.json());
  } catch {
    return [];
  }
}

// Fetch the first page of templates for the New Project picker.
export function fetchTemplates(): Promise<TemplateSummary[]> {
  return fetchContentList('templates');
}

// Fetch one template (including its modelXmile) by slug, or null on failure.
export async function fetchTemplate(slug: string): Promise<TemplateDetail | null> {
  try {
    const res = await fetch(`${API_BASE}/templates/${encodeURIComponent(slug)}`, { credentials: 'same-origin' });
    if (!res.ok) {
      return null;
    }
    return parseTemplateDetail(await res.json());
  } catch {
    return null;
  }
}
